import { SPACE } from '@HoudiniUI/Theme/theme';
import { THEME } from '@Theme';
import { StyleSheet } from 'aphrodite';

export const styleSheet = StyleSheet.create({
	campaignName: {
		fontSize: THEME.typography.fontSizes.lg,
		fontWeight: THEME.typography.fontWeights.bold,
		margin: 0,
	},
	container: {
		boxSizing: 'border-box',
		display: 'flex',
		gap: THEME.spacing.lg,
		height: '100%',
		padding: THEME.spacing.md,
		width: '100%',
	},
	editActions: {
		display: 'flex',
		gap: THEME.spacing.sm,
		justifyContent: 'flex-end',
	},
	// TinyMCE is configured with height 100%, which resolves to auto against a parent without a definite height,
	// collapsing the edit area; the editable editor needs a real one.
	//
	// The border is ours to draw: RichContentDocumentEditor strips TinyMCE's own chrome globally
	// (`.rich-content-document-editor .tox.tox-tinymce { border: none }`), so an editor sitting on a white card has
	// no edge unless its wrapper supplies one. Without it the rounding below had nothing to round.
	editableEditor: {
		background: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		flex: 1,
		minHeight: 420,
	},
	editorSubject: {
		width: '100%',
	},
	emptyBody: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
		margin: 0,
		maxWidth: 480,
	},
	emptyHeadline: {
		fontSize: THEME.typography.fontSizes.md,
		fontWeight: THEME.typography.fontWeights.bold,
		margin: 0,
	},
	emptyState: {
		alignItems: 'center',
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		gap: THEME.spacing.sm,
		justifyContent: 'center',
		textAlign: 'center',
	},
	cancelLink: {
		background: 'none',
		border: 'none',
		color: THEME.colors.danger.main,
		cursor: 'pointer',
		fontSize: THEME.typography.fontSizes.sm,
		padding: 0,
	},
	leftOutCard: {
		background: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		display: 'flex',
		flex: '0 1 auto',
		flexDirection: 'column',
		gap: SPACE[8],
		maxHeight: '40%',
		minHeight: 0,
		padding: SPACE[16],
	},
	leftOutHeading: {
		alignItems: 'center',
		display: 'flex',
		gap: SPACE[8],
		justifyContent: 'space-between',
		minWidth: 0,
	},
	leftOutReason: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
	},
	leftOutRow: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[4],
		padding: SPACE[8],
	},
	noticeBody: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.sm,
		margin: 0,
	},
	noticeCard: {
		background: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.md,
		padding: THEME.spacing.md,
	},
	noticeHeadline: {
		fontSize: THEME.typography.fontSizes.md,
		fontWeight: THEME.typography.fontWeights.bold,
		margin: 0,
	},
	previewActions: {
		display: 'flex',
		flexShrink: 0,
		gap: THEME.spacing.sm,
	},
	previewBody: {
		background: THEME.colors.background.light,
		flex: 1,
		minHeight: 0,
		overflowY: 'auto',
	},
	previewHeader: {
		alignItems: 'flex-start',
		borderBottom: `1px solid ${THEME.colors.border.light}`,
		display: 'flex',
		gap: THEME.spacing.md,
		justifyContent: 'space-between',
		paddingBottom: THEME.spacing.sm,
	},
	previewMeta: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
		minWidth: 0,
	},
	previewPane: {
		background: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		boxSizing: 'border-box',
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		gap: THEME.spacing.md,
		minWidth: 0,
		overflow: 'hidden',
		padding: THEME.spacing.lg,
	},
	previewRecipient: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.sm,
	},
	previewSubject: {
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	rail: {
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		flexShrink: 0,
		gap: THEME.spacing.md,
		height: '100%',
		minHeight: 0,
		width: 360,
	},
	recipientMissing: {
		color: THEME.colors.danger.main,
		fontSize: THEME.typography.fontSizes.xs,
	},
	recipientName: {
		fontWeight: THEME.typography.fontWeights.bold,
		...THEME.common.truncate,
	},
	recipientRow: {
		background: 'none',
		border: 'none',
		borderRadius: THEME.common.borderRadius,
		cursor: 'pointer',
		display: 'flex',
		flexDirection: 'column',
		gap: 2,
		padding: THEME.spacing.sm,
		textAlign: 'left',
		width: '100%',
		':hover': {
			background: THEME.colors.background.main,
		},
	},
	recipientRowSelected: {
		background: THEME.colors.background.main,
		boxShadow: `inset 3px 0 0 ${THEME.colors.primary.main}`,
	},
	recipientsCard: {
		background: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		gap: THEME.spacing.sm,
		minHeight: 0,
		padding: THEME.spacing.md,
	},
	recipientsList: {
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		gap: THEME.spacing.xs,
		minHeight: 0,
		overflowY: 'auto',
	},
	recipientsSummary: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
	},
	recipientsTitle: {
		fontSize: THEME.typography.fontSizes.md,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	recipientSubject: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
		width: '100%',
		...THEME.common.truncate,
	},
	scheduleActions: {
		display: 'flex',
		gap: THEME.spacing.md,
	},
	scheduleCard: {
		background: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		display: 'flex',
		flexDirection: 'column',
		flexShrink: 0,
		fontSize: THEME.typography.fontSizes.sm,
		gap: THEME.spacing.xs,
		padding: THEME.spacing.md,
	},
	scheduleTitle: {
		fontSize: THEME.typography.fontSizes.md,
		fontWeight: THEME.typography.fontWeights.bold,
	},
});
